import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Key,
  Link as LinkIcon,
  Building2,
  Copy,
  Check,
  Eye,
  EyeOff,
  RefreshCw,
  Save,
  AlertTriangle,
  Percent,
  Info,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as merchantService from "@/shared/api/services/merchantService";

export default function MerchantSettings() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const { data: merchantProfile } = useQuery({
    queryKey: ["merchant-profile"],
    queryFn: () => merchantService.getProfile(),
  });
  const [showSecret, setShowSecret] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [callbackUrl, setCallbackUrl] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [savingUrls, setSavingUrls] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [regeneratingKeys, setRegeneratingKeys] = useState(false);

  const [profile, setProfile] = useState({
    businessName: "",
    email: "",
  });

  useEffect(() => {
    if (!merchantProfile) {
      return;
    }

    setCallbackUrl(merchantProfile.callbackUrl ?? "");
    setWebhookUrl(merchantProfile.webhookUrl ?? "");
    setProfile({
      businessName: merchantProfile.name ?? "",
      email: merchantProfile.email ?? "",
    });
  }, [merchantProfile]);

  const copyToClipboard = (text, keyType) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyType);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const maskKey = (key) => {
    return key.slice(0, 12) + "••••••••••••••••" + key.slice(-4);
  };

  const handleSaveUrls = async () => {
    setSavingUrls(true);
    try {
      await merchantService.updateProfile({ callbackUrl, webhookUrl });
      await queryClient.invalidateQueries({ queryKey: ["merchant-profile"] });
    } finally {
      setSavingUrls(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await merchantService.updateProfile({ name: profile.businessName });
      await queryClient.invalidateQueries({ queryKey: ["merchant-profile"] });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveAll = async () => {
    await Promise.all([handleSaveUrls(), handleSaveProfile()]);
  };

  const handleRegenerateKeys = async () => {
    setRegeneratingKeys(true);
    try {
      await merchantService.regenerateApiKeys();
      await queryClient.invalidateQueries({ queryKey: ["merchant-profile"] });
    } finally {
      setRegeneratingKeys(false);
    }
  };

  const apiKey = merchantProfile?.apiKey || "-";
  const secretKey = merchantProfile?.secretKey || "-";

  return (
    <MerchantLayout currentPage="MerchantSettings">
      <div className="max-w-3xl mx-auto px-3 sm:px-4 lg:px-0 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("settings")}</h1>
            <p className="text-slate-500">{t("manageProfile")}</p>
          </div>
          <Button onClick={handleSaveAll} disabled={savingUrls || savingProfile} className="gap-2 w-full sm:w-auto">
            {savingUrls || savingProfile ? (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {t("saveChanges")}
          </Button>
        </div>

        {/* Business Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle>{t("merchantProfile")}</CardTitle>
                  <CardDescription>{t("yourBusinessInfo")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="businessName">{t("businessName")}</Label>
                <Input
                  id="businessName"
                  value={profile.businessName}
                  onChange={(e) => setProfile({ ...profile, businessName: e.target.value })}
                  className="text-sm sm:text-base"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("emailAddress")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  disabled
                  className="bg-slate-50"
                />
              </div>

            </CardContent>
          </Card>
        </motion.div>

        {/* Callback URLs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <LinkIcon className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle>{t("callbackUrls")}</CardTitle>
                  <CardDescription>{t("whereWeSendNotifications")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="callback">{t("callbackUrl")}</Label>
                <Input
                  id="callback"
                  value={callbackUrl}
                  onChange={(e) => setCallbackUrl(e.target.value)}
                  placeholder={t("callbackUrlPlaceholder")}
                  className="text-sm sm:text-base"
                />
                <p className="text-xs text-slate-500">
                  {t("wePostToEndpoint")}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhook">{t("webhookUrl")}</Label>
                <Input
                  id="webhook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder={t("webhookUrlPlaceholder")}
                  className="text-sm sm:text-base"
                />
                <p className="text-xs text-slate-500">
                  {t("additionalEventNotifications")}
                </p>
              </div>

            </CardContent>
          </Card>
        </motion.div>

        {/* API Keys */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Key className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>{t("apiKeys")}</CardTitle>
                  <CardDescription>{t("yourAuthCredentials")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* API Key */}
              <div className="space-y-2">
                <Label>{t("apiKeyPublic")}</Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="min-w-0 flex-1 rounded-lg border bg-slate-50 px-4 py-2.5 overflow-x-auto">
                    <code className="block whitespace-nowrap text-sm font-mono">
                      {apiKey}
                    </code>
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 self-start sm:self-auto"
                    onClick={() => copyToClipboard(apiKey, "api")}
                  >
                    {copiedKey === "api" ? (
                      <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Secret Key */}
              <div className="space-y-2">
                <Label>{t("secretKey")}</Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="min-w-0 flex-1 rounded-lg border bg-slate-50 px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1 overflow-x-auto">
                        <code className="block whitespace-nowrap text-sm font-mono">
                          {showSecret ? secretKey : maskKey(secretKey)}
                        </code>
                      </div>

                      <button
                        onClick={() => setShowSecret(!showSecret)}
                        className="ml-2 shrink-0 text-slate-400 hover:text-slate-600"
                        type="button"
                      >
                        {showSecret ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 self-start sm:self-auto"
                    onClick={() => copyToClipboard(secretKey, "secret")}
                  >
                    {copiedKey === "secret" ? (
                      <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Regenerate */}
              <div className="pt-4 border-t flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">{t("regenerateKeys")}</p>
                  <p className="text-xs text-slate-500">{t("invalidateCurrentKeys")}</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="gap-2 text-amber-600 hover:text-amber-700">
                      <RefreshCw className="h-4 w-4" />
                      {t("regenerate")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        {t("regenerateApiKeys")}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("invalidateImmediately")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleRegenerateKeys}
                        disabled={regeneratingKeys}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        {regeneratingKeys ? (
                          <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          t("regenerateKeys")
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Fee Card */}
        {merchantProfile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Percent className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle>Fee</CardTitle>
                    <CardDescription>Fee information</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Parent Fee Info */}
                {merchantProfile.parentName && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex gap-3">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-900 mb-2">
                          {t("assignedBy")} {merchantProfile.parentType}
                        </h4>
                        <div className="text-sm">
                          <div>
                            <p className="text-blue-700 font-medium">Fee</p>
                            <p className="text-blue-900 text-lg font-semibold">
                              {merchantProfile.parentPercentageFee !== null && merchantProfile.parentPercentageFee !== undefined
                                ? `${merchantProfile.parentPercentageFee}%`
                                : t("nA")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Fee (Read-only) */}
                <div className="space-y-4 pt-4 border-t">
                  <div>
                    <Label className="text-sm font-medium text-slate-900">Your Fee</Label>
                    <p className="text-xs text-slate-500 mt-1">Fee assigned to you</p>
                  </div>
                  <div>
                    <div className="p-4 rounded-lg bg-gradient-to-br from-violet-50 to-violet-50 border border-violet-200 hover:border-violet-300 transition-colors">
                      <p className="text-xs font-medium text-violet-700 mb-2">Fee</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-violet-900">
                          {merchantProfile.percentageFee !== null && merchantProfile.percentageFee !== undefined
                            ? merchantProfile.percentageFee
                            : t("nA")}
                        </span>
                        {merchantProfile.percentageFee !== null && merchantProfile.percentageFee !== undefined && (
                          <span className="text-sm text-violet-700">%</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

      </div>
    </MerchantLayout>
  );
}